import { useState, useEffect } from 'react';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from 'next-themes';
import { useAuth } from '@/hooks/useAuth';

// Scope the "already completed" markers by userId so a previous account on
// the same browser doesn't prevent a new account from seeing the tour. The
// "welcome shown" key is also userId-scoped (set by WelcomeDialog) — read
// it the same way here to keep them in sync.
const tourCompletedKeyFor = (userId: string | null | undefined) =>
  userId ? `product-tour-completed:${userId}` : null;
const welcomeShownKeyFor = (userId: string | null | undefined) =>
  userId ? `welcome_dialog_shown:${userId}` : null;

interface ProductTourProps {
  onComplete?: () => void;
  isLoading?: boolean;
}

export const ProductTour = ({ onComplete, isLoading = false }: ProductTourProps) => {
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const { user } = useAuth();
  const userId = user?.id;

  // Check and start tour when conditions are met
  useEffect(() => {
    const checkAndStartTour = () => {
      const tourKey = tourCompletedKeyFor(userId);
      const welcomeKey = welcomeShownKeyFor(userId);
      const tourCompleted = tourKey ? localStorage.getItem(tourKey) : null;
      const welcomeShown = welcomeKey ? localStorage.getItem(welcomeKey) : null;
      const forceRun = localStorage.getItem('product-tour-force') === 'true';
      const onDashboard = location.pathname === '/panel' || location.pathname === '/dashboard';

      // Start immediately when: welcome done AND not completed AND not loading OR forced restart
      if (!isLoading && onDashboard && !run) {
        if ((welcomeShown === 'true' && !tourCompleted) || forceRun) {
          console.info('[ProductTour] Starting tour', { forceRun, onDashboard, welcomeShown, tourCompleted });
          setRun(true);
          setStepIndex(0);
          if (forceRun) {
            localStorage.removeItem('product-tour-force');
          }
          return true; // Tour started
        }
      }
      return false;
    };

    // Initial check
    if (checkAndStartTour()) return;

    // Poll for welcome_dialog_shown changes (when waiting for welcome dialog to close)
    const onDashboard = location.pathname === '/panel' || location.pathname === '/dashboard';
    const tourKey = tourCompletedKeyFor(userId);
    const tourCompleted = tourKey ? localStorage.getItem(tourKey) : null;
    const onboardingComplete = localStorage.getItem('onboarding_completed');

    // Only poll if we're on dashboard, haven't completed tour, and onboarding is done
    if (onDashboard && !tourCompleted && onboardingComplete === 'true' && !isLoading) {
      const interval = setInterval(() => {
        if (checkAndStartTour()) {
          clearInterval(interval);
        }
      }, 500);

      return () => clearInterval(interval);
    }
  }, [location.pathname, isLoading, run, userId]);

  const steps: Step[] = [
    {
      target: 'body',
      content: (
        <div className="space-y-3">
          <div className="text-3xl mb-3">👋</div>
          <h3 className="text-xl font-bold text-foreground">{t('tour.welcome.title')}</h3>
          <p className="text-muted-foreground leading-relaxed">{t('tour.welcome.description')}</p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '[data-tour="profile-stats"]',
      content: (
        <p className="text-muted-foreground leading-relaxed">{t('tour.profileStats.description')}</p>
      ),
      title: t('tour.profileStats.title'),
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour="competitors-section"]',
      content: (
        <p className="text-muted-foreground leading-relaxed">{t('tour.competitors.description')}</p>
      ),
      title: t('tour.competitors.title'),
      placement: 'bottom',
      disableBeacon: true,
    },
    {
      target: '[data-tour="featured-ideas"]',
      content: (
        <p className="text-muted-foreground leading-relaxed">{t('tour.featuredIdeas.description')}</p>
      ),
      title: t('tour.featuredIdeas.title'),
      placement: 'auto',
      disableBeacon: true,
    },
    {
      target: '[href="/biblioteka"]',
      content: (
        <p className="text-muted-foreground leading-relaxed">{t('tour.library.description')}</p>
      ),
      title: t('tour.library.title'),
      placement: 'right',
      disableBeacon: true,
    },
    {
      target: '[href="/ulubione"]',
      content: (
        <p className="text-muted-foreground leading-relaxed">{t('tour.favoritesPage.description')}</p>
      ),
      title: t('tour.favoritesPage.title'),
      placement: 'right',
      disableBeacon: true,
    },
    {
      target: '[href="/inspiracje"]',
      content: (
        <p className="text-muted-foreground leading-relaxed">{t('tour.inspiration.description')}</p>
      ),
      title: t('tour.inspiration.title'),
      placement: 'right',
      disableBeacon: true,
    },
    {
      target: '[href="/analityka"]',
      content: (
        <p className="text-muted-foreground leading-relaxed">{t('tour.analytics.description')}</p>
      ),
      title: t('tour.analytics.title'),
      placement: 'right',
      disableBeacon: true,
    },
    {
      target: '[href="/konkurencja"]',
      content: (
        <p className="text-muted-foreground leading-relaxed">{t('tour.competitors.navDescription')}</p>
      ),
      title: t('tour.competitors.navTitle'),
      placement: 'right',
      disableBeacon: true,
    },
  ];

  const markTourCompleted = () => {
    const tourKey = tourCompletedKeyFor(userId);
    if (tourKey) localStorage.setItem(tourKey, 'true');
  };

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, index, action, type } = data;

    // Handle close button click - close the entire tour
    // The close button triggers type 'tooltip' with action 'close'
    if (type === 'tooltip' && (data as { action: string }).action === 'close') {
      markTourCompleted();
      setRun(false);
      setStepIndex(0);
      onComplete?.();
      return;
    }

    if (action === 'update' && index !== undefined) {
      setStepIndex(index);
    }

    if (([STATUS.FINISHED, STATUS.SKIPPED] as string[]).includes(status)) {
      markTourCompleted();
      setRun(false);
      setStepIndex(0);
      onComplete?.();
    }
  };

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showProgress={false}
      showSkipButton
      callback={handleJoyrideCallback}
      styles={{
        options: {
          primaryColor: isDark ? 'hsl(210 100% 55%)' : 'hsl(var(--primary))',
          textColor: isDark ? 'hsl(210 40% 98%)' : 'hsl(var(--foreground))',
          backgroundColor: isDark ? 'hsl(222 47% 12%)' : 'rgba(255, 255, 255, 0.85)',
          overlayColor: 'rgba(0, 0, 0, 0.7)',
          arrowColor: isDark ? 'hsl(222 47% 12%)' : 'rgba(255, 255, 255, 0.85)',
          zIndex: 10000,
        },
        tooltip: {
          borderRadius: '20px',
          padding: '28px',
          boxShadow: isDark
            ? '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.08)'
            : '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)',
          border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(255, 255, 255, 0.2)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          background: isDark ? 'hsl(222 47% 12%)' : 'rgba(255, 255, 255, 0.9)',
        },
        tooltipTitle: {
          fontSize: '17px',
          fontWeight: '600',
          marginBottom: '4px',
          color: isDark ? 'hsl(210 40% 98%)' : 'hsl(var(--foreground))',
          letterSpacing: '-0.01em',
        },
        tooltipContent: {
          fontSize: '14px',
          lineHeight: '1.6',
          padding: '4px 0',
          color: isDark ? 'hsl(215 20% 65%)' : 'hsl(var(--muted-foreground))',
        },
        buttonNext: {
          backgroundColor: 'hsl(210 100% 55%)',
          color: '#fff',
          borderRadius: '12px',
          padding: '10px 24px',
          fontSize: '14px',
          fontWeight: '600',
          boxShadow: '0 4px 14px hsla(210 100% 55% / 0.4)',
          transition: 'all 0.2s ease',
          border: 'none',
        },
        buttonBack: {
          color: isDark ? 'hsl(215 20% 65%)' : 'hsl(var(--muted-foreground))',
          marginRight: '12px',
          fontSize: '14px',
          fontWeight: '500',
          background: 'transparent',
          border: 'none',
        },
        buttonSkip: {
          color: isDark ? 'hsl(215 20% 65%)' : 'hsl(var(--muted-foreground))',
          fontSize: '13px',
          fontWeight: '500',
          opacity: 0.7,
        },
        buttonClose: {
          display: 'none',
        },
        spotlight: {
          borderRadius: '16px',
          boxShadow: '0 0 0 4px rgba(99, 102, 241, 0.2)',
        },
      }}
      locale={{
        back: t('tour.buttons.back'),
        close: t('tour.buttons.close'),
        last: t('tour.buttons.finish'),
        next: t('tour.buttons.next'),
        skip: t('tour.buttons.skip'),
      }}
    />
  );
};
